import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../stores/notificationStore';
import type { Notification } from '../../api/notifications';
import styles from './NotificationPanel.module.css';

const TYPE_COLORS: Record<string, string> = {
  comment: '#22d3ee',
  approval: '#34d399',
  project_update: '#a78bfa',
  invitation: '#fbbf24',
  system: '#fb7185',
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'maintenant';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}j`;
  return new Date(iso).toLocaleDateString('fr-CH');
}

export function NotificationPanel() {
  const navigate = useNavigate();
  const notifications = useNotificationStore((s) => s.notifications);
  const loading = useNotificationStore((s) => s.loading);
  const isOpen = useNotificationStore((s) => s.isOpen);
  const closePanel = useNotificationStore((s) => s.closePanel);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  if (!isOpen) return null;

  function handleClick(notif: Notification) {
    if (!notif.is_read) markAsRead(notif.id);

    // Navigate to resource if available
    if (notif.resource_type === 'project' && notif.resource_id) {
      navigate(`/app/projects/${notif.resource_id}`);
      closePanel();
    }
  }

  return (
    <>
      <div className={styles.overlay} onClick={closePanel} />
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </span>
          <div className={styles.headerActions}>
            {unreadCount > 0 && (
              <button className={styles.markAllBtn} onClick={markAllAsRead}>
                Tout marquer lu
              </button>
            )}
          </div>
        </div>

        <div className={styles.list}>
          {loading ? (
            <div className={styles.loading}>Chargement...</div>
          ) : notifications.length === 0 ? (
            <div className={styles.empty}>Aucune notification</div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`${styles.item} ${!notif.is_read ? styles.itemUnread : ''}`}
                onClick={() => handleClick(notif)}
              >
                <div className={`${styles.dot} ${notif.is_read ? styles.dotRead : ''}`} />
                <div className={styles.content}>
                  <div className={styles.title}>
                    <span
                      className={styles.typeBadge}
                      style={{ background: `${TYPE_COLORS[notif.type] || '#6366f1'}22`, color: TYPE_COLORS[notif.type] || '#6366f1' }}
                    >
                      {notif.type}
                    </span>
                    {notif.title}
                  </div>
                  <div className={styles.message}>{notif.message}</div>
                  <div className={styles.time}>{timeAgo(notif.created_at)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
